import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { CenteredLoader } from "@/components/shared/Loader";
import { resolveLandingPath, isRestorableTarget } from "./landing";

/**
 * GuestGuard — the mirror of AuthGuard, for the sign-in screens.
 *
 * The auth routes had no guard at all, so a signed-in user who typed /login,
 * used a bookmark, or pressed Back after signing in got the login form again,
 * over a live session. Filling it in either signed them in a second time or
 * failed for a reason that made no sense.
 *
 * Waiting on `isLoading` matters as much as the redirect: bootstrap renews the
 * session before it can say who this is, and rendering the login form during
 * that window shows the form to someone who is about to turn out to be signed
 * in — a flash of the wrong screen on every reload.
 *
 * Where they go instead comes from `resolveLandingPath`, so it is the same
 * destination the login screen itself would have used.
 */
const GuestGuard = () => {
  const { isAuthenticated, isLoading, authChecked, user } = useAuth();
  const location = useLocation();

  if (isLoading || !authChecked) {
    return (
      <div className="min-h-screen bg-background-primary">
        <CenteredLoader />
      </div>
    );
  }

  if (!isAuthenticated) return <Outlet />;

  // Honour a deep link captured when they were bounced to /login, but only if
  // it is somewhere this account may actually go — otherwise send them home.
  const requested = location.state?.from?.pathname;
  const target = isRestorableTarget(requested, user) ? requested : resolveLandingPath(user);

  return <Navigate to={target} replace />;
};

export default GuestGuard;
